<template>
  <dashboard-layout title="Intent Log" back-to="/signals" back-label="Signals">
    <template v-if="log">
      <data-panel title="Overview">
        <div class="badges-row">
          <status-badge variant="success" :label="log.intent" />
          <status-badge variant="primary" :label="log.source" />
        </div>
        <div class="meta-info">
          <span class="date">{{ formatTimestamp(log.timestamp) }}</span>
        </div>
      </data-panel>

      <data-panel title="Message">
        <div class="message-text">{{ log.message }}</div>
      </data-panel>

      <data-panel v-if="userData" title="User Information">
        <div class="user-info">
          <div class="user-avatar">{{ getUserInitials(userData.properties?.name) }}</div>
          <div class="user-details">
            <div class="user-name">{{ userData.properties?.name || 'Unknown User' }}</div>
            <div class="user-email">{{ userData.properties?.email || log.userId }}</div>
            <router-link :to="`/user/${log.userId}`" class="view-profile-link">
              View Full Profile
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <circle cx="12" cy="10" r="3"></circle>
                <path d="M7 20.662V19c0-1 .9-2 2-2h6c1.1 0 2 1 2 2v1.662"></path>
              </svg>
            </router-link>
          </div>
        </div>
      </data-panel>

      <data-panel title="Additional Information">
        <div class="metadata-grid">
          <div class="metadata-item">
            <label>Log ID</label>
            <span>{{ log.id }}</span>
          </div>
          <div class="metadata-item">
            <label>User ID</label>
            <router-link :to="`/user/${log.userId}`" class="user-link">
              {{ log.userId }}
            </router-link>
          </div>
          <div class="metadata-item">
            <label>Intent Type</label>
            <span>{{ log.intent }}</span>
          </div>
          <div class="metadata-item">
            <label>Source</label>
            <span>{{ log.source }}</span>
          </div>
        </div>
      </data-panel>
    </template>

    <loading-state v-else-if="loading" label="Loading intent log details…" />

    <div v-else class="not-found">
      <empty-state
        title="Intent log not found"
        hint="Intent log not found or an error occurred."
        icon="inbox"
      />
      <router-link to="/signals" class="back-link">Return to Signals</router-link>
    </div>
  </dashboard-layout>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { FirebaseIntentLogService } from '../services/firebaseIntentLogService'
import { FirebaseUserService } from '../services/firebaseUserService'
import DashboardLayout from './ui/DashboardLayout.vue'
import DataPanel from './ui/DataPanel.vue'
import StatusBadge from './ui/StatusBadge.vue'
import LoadingState from './ui/LoadingState.vue'
import EmptyState from './ui/EmptyState.vue'

export default {
  name: 'IntentLogDetailView',
  components: {
    DashboardLayout, DataPanel, StatusBadge, LoadingState, EmptyState
  },
  setup() {
    const route = useRoute()
    const logService = new FirebaseIntentLogService()
    const userService = new FirebaseUserService()

    const log = ref(null)
    const userData = ref(null)
    const loading = ref(true)

    const loadLog = async () => {
      try {
        const logId = route.params.id
        const logData = await logService.getIntentLogById(logId)
        log.value = logData

        if (logData && logData.userId) {
          const user = await userService.getUserById(logData.userId)
          userData.value = user
        }
      } catch (error) {
        console.error('Error loading intent log:', error)
      } finally {
        loading.value = false
      }
    }

    const formatTimestamp = (timestamp) => {
      if (!timestamp) return '-'

      if (timestamp.seconds) {
        const date = new Date(timestamp.seconds * 1000)
        return date.toLocaleString('tr-TR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        })
      }

      const date = new Date(timestamp)
      return date.toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }

    const getUserInitials = (name) => {
      if (!name) return '?'
      return name.split(' ').map(n => n[0]).join('').toUpperCase()
    }

    onMounted(() => {
      loadLog()
    })

    return {
      log,
      userData,
      loading,
      formatTimestamp,
      getUserInitials
    }
  }
}
</script>

<style scoped>
.badges-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.meta-info {
  display: flex;
  gap: 16px;
  color: var(--text-secondary);
  font-size: 14px;
}

.message-text {
  background: rgba(255, 255, 255, 0.05);
  padding: 20px;
  border-radius: 12px;
  line-height: 1.7;
  color: var(--text-primary);
  white-space: pre-wrap;
  font-size: 15px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.user-info {
  display: flex;
  align-items: center;
  gap: 16px;
  background: rgba(255, 255, 255, 0.05);
  padding: 20px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.user-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-color), var(--gradient-2));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 600;
  color: white;
  flex-shrink: 0;
}

.user-details {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.user-name {
  font-weight: 600;
  font-size: 16px;
  color: var(--text-primary);
}

.user-email {
  color: var(--text-secondary);
  font-size: 14px;
}

.view-profile-link {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--primary-color);
  font-size: 14px;
  text-decoration: none;
  transition: all 0.3s ease;
  width: fit-content;
  margin-top: 4px;
}

.view-profile-link:hover {
  opacity: 0.8;
  text-decoration: underline;
}

.metadata-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  background: rgba(255, 255, 255, 0.05);
  padding: 20px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.metadata-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.metadata-item label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-secondary);
  font-weight: 600;
}

.metadata-item span,
.metadata-item .user-link {
  color: var(--text-primary);
  font-size: 14px;
  word-break: break-all;
}

.user-link {
  color: var(--primary-color);
  text-decoration: none;
  transition: all 0.2s ease;
}

.user-link:hover {
  text-decoration: underline;
  opacity: 0.8;
}

.not-found {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.back-link {
  margin-top: 8px;
  padding: 8px 16px;
  background: rgba(108, 99, 255, 0.2);
  border-radius: 8px;
  color: var(--primary-color);
  text-decoration: none;
  transition: all 0.3s ease;
}

.back-link:hover {
  background: rgba(108, 99, 255, 0.3);
}

@media (max-width: 1024px) {
  .metadata-grid {
    grid-template-columns: 1fr;
  }

  .user-info {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
